import { Injectable } from '@angular/core';
import { HttpService } from '@app/@core/services/http.service';
import { LocalService } from '@app/@core/services/local.service';
import { PageBuilderAddService } from '../@core/page-builder-add.service';
import {
  RuleBuilderCondition,
  RuleBuilderConditionGroup,
  RuleBuilderFieldOption,
  RuleBuilderOption,
  RuleBuilderPanelOption,
  RuleBuilderTabOption,
  RuleBuilderWizardPageOption,
  SupportingDocumentRuleApiQuery,
  SupportingDocumentRuleRecord
} from './supporting-document-rule-config.models';
import { Observable, forkJoin, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs';
import { PageBuilderAddURL } from '@app/pages/page-builder/page-builder/page-builder-add/@core/page-builder-add-url.config';
import { SupportingDocumentGridOption, SupportingDocumentGridStatusConfig } from '../supporting-document-grid/supporting-document-grid-config.model';

@Injectable({
  providedIn: 'root'
})
export class SupportingDocumentRuleConfigService {
  private readonly baseEndpoint = '/application-flow/supporting-document-rules';
  private readonly supportingDocumentCategoryCacheKey = 'supportingDocumentCategoriesCache';
  private readonly supportingDocumentTypeCacheKey = 'supportingDocumentTypesCache';
  private readonly supportingDocumentStatusCacheKey = 'supportingDocumentStatusesCache';
  private supportingDocumentCategoriesCache: SupportingDocumentGridOption[] | null = null;
  private supportingDocumentTypesCache: Record<string, SupportingDocumentGridOption[]> = {};
  private categoriesRequest$: Observable<SupportingDocumentGridOption[]> | null = null;
  private typeRequestCache: Record<string, Observable<SupportingDocumentGridOption[]>> = {};
  private dynamicLookupRequestCache: Record<string, Observable<SupportingDocumentGridOption[]>> = {};
  private dynamicLookupCache: Record<string, SupportingDocumentGridOption[]> = {};
  private documentStatusesRequest$: Observable<SupportingDocumentGridStatusConfig> | null = null;
  private documentStatusesCache: SupportingDocumentGridStatusConfig | null = null;
  constructor(
    private readonly httpService: HttpService,
    private readonly pageBuilderService: PageBuilderAddService,
    private readonly localService: LocalService,
  ) {}

  getPages(): Observable<RuleBuilderWizardPageOption[]> {
    return this.httpService.get(PageBuilderAddURL.EndPoints.supporting_document_rule.getAllWizardForms).pipe(
      map((response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        return rows.map((row: any) => ({
          wizardPageName: row?.wizardPageName ?? row?.page_name ?? row?.name ?? `Page ${row?.id}`,
          stepperId: String( row?.stepperId ?? row?.stepperVersionId ?? row?.pageversion_id ?? ''),
          stepperVersionId: String(row?.stepperVersionId ?? row?.pageversion_id ?? row?.activeversionid ?? row?.id ?? '')
        })).filter((row: RuleBuilderWizardPageOption) => !!row.stepperId);
      })
    );
  }

  getDocumentLookupData(): Observable<{ categories: RuleBuilderOption[]; documentTypes: RuleBuilderOption[] }> {
    return forkJoin({
      categories: this.httpService.get('/domain/lookup?requestType=DOCUMENT CATEGORY'),
      documentTypes: this.httpService.get('/domain/lookup?requestType=DOCUMENT TYPE')
    }).pipe(
      map((response: any) => ({
        categories: this.toLookupOptions(response.categories?.data),
        documentTypes: this.toLookupOptions(response.documentTypes?.data)
      }))
    );
  }

  getSupportingDocumentCategoriesWithTypes(): Observable<SupportingDocumentGridOption[]> {
    return this.getSupportingDocumentCategories().pipe(
      switchMap((categories: SupportingDocumentGridOption[]) => {
        if (!categories.length) {
          return of([]);
        }
        const requests = categories.map((category: SupportingDocumentGridOption) =>
          this.getSupportingDocumentTypesForCategory(category)
        );
        return forkJoin(requests).pipe(
          map((typeGroups: SupportingDocumentGridOption[][]) =>
            categories.map((category: SupportingDocumentGridOption, index: number) => ({
              ...category,
              lookup: typeGroups[index] || []
            }))
          ),
          tap((categoriesWithTypes: SupportingDocumentGridOption[]) => {
            this.supportingDocumentCategoriesCache = categoriesWithTypes;
            this.persistSupportingDocumentCategories(categoriesWithTypes);
          })
        );
      })
    );
  }

  getSupportingDocumentCategories(): Observable<SupportingDocumentGridOption[]> {
    const memoryCache = this.supportingDocumentCategoriesCache;
    if (memoryCache?.length) {
      return of(memoryCache);
    }

    const storedCategories = this.readStoredSupportingDocumentCategories();
    if (storedCategories.length) {
      this.supportingDocumentCategoriesCache = storedCategories;
      return of(storedCategories);
    }

    if (this.categoriesRequest$) {
      return this.categoriesRequest$;
    }

    this.categoriesRequest$ = this.httpService.get('/platform/master/lookup/category/SUPPORTING-DOCUMENT-CATEGORY').pipe(
      map((response: any) => this.extractLookupRows(response).map((item: any) => this.toSupportingDocumentOption(item))),
      tap((categories: SupportingDocumentGridOption[]) => {
        this.supportingDocumentCategoriesCache = categories;
        this.persistSupportingDocumentCategories(categories);
      }),
      shareReplay(1)
    );

    return this.categoriesRequest$;
  }

  getSupportingDocumentTypesForCategory(category: SupportingDocumentGridOption | string, parentId?: string): Observable<SupportingDocumentGridOption[]> {
    const categoryKey = typeof category === 'string' ? category : String(category?.key || category?.value || '').trim();
    // const resolvedParentId = String(parentId || (typeof category === 'string' ? '' : category?.lookupId) || '').trim();
    const cacheKey = `${categoryKey}`;

    if (this.supportingDocumentTypesCache[cacheKey]?.length) {
      return of(this.supportingDocumentTypesCache[cacheKey]);
    }

    const storedTypes = this.readStoredSupportingDocumentTypes()[cacheKey];
    if (storedTypes?.length) {
      this.supportingDocumentTypesCache[cacheKey] = storedTypes;
      return of(storedTypes);
    }

    if (!categoryKey) {
      return of([]);
    }

    if (this.typeRequestCache[cacheKey]) {
      return this.typeRequestCache[cacheKey];
    }

    const endpoint = `/platform/master/lookup/category/SUPPORTING-DOCUMENT-CATEGORY?parent=${encodeURIComponent(categoryKey)}`;
    this.typeRequestCache[cacheKey] = this.httpService.get(endpoint).pipe(
      map((response: any) => this.extractLookupRows(response).map((item: any) => this.toSupportingDocumentOption(item))),
      tap((types: SupportingDocumentGridOption[]) => {
        this.supportingDocumentTypesCache[cacheKey] = types;
        this.persistSupportingDocumentTypes();
      }),
      shareReplay(1)
    );

    return this.typeRequestCache[cacheKey];
  }

  getCategoriesBySectionName(sectionName: string): Observable<SupportingDocumentGridOption[]> {
    return this.getSupportingDocumentCategories().pipe(
      map((items: SupportingDocumentGridOption[]) =>
        items.filter(
          (item: SupportingDocumentGridOption) =>
            !item.parentId && item.optionaldata?.section_name === sectionName
        )
      )
    );
  }

  getTypesByCategory(category: SupportingDocumentGridOption): Observable<SupportingDocumentGridOption[]> {
    if (!category?.key) {
      return of([]);
    }
    return this.getSupportingDocumentTypesForCategory(category);
  }

  getAllTopLevelCategories(): Observable<SupportingDocumentGridOption[]> {
    return this.getSupportingDocumentCategories().pipe(
      map((items: SupportingDocumentGridOption[]) =>
        items.filter((item: SupportingDocumentGridOption) => !item.parentId)
      )
    );
  }

  getLookupOptionsByRequestType(requestType: string): Observable<SupportingDocumentGridOption[]> {
    const cacheKey = String(requestType || '').trim();
    if (!cacheKey) {
      return of([]);
    }

    if (this.dynamicLookupCache[cacheKey]?.length) {
      return of(this.dynamicLookupCache[cacheKey]);
    }

    if (this.dynamicLookupRequestCache[cacheKey]) {
      return this.dynamicLookupRequestCache[cacheKey];
    }

    this.dynamicLookupRequestCache[cacheKey] = this.httpService.get(`/domain/lookup?requestType=${encodeURIComponent(cacheKey)}`).pipe(
      map((response: any) => this.extractLookupRows(response).map((item: any) => this.toSupportingDocumentOption(item))),
      tap((options: SupportingDocumentGridOption[]) => {
        this.dynamicLookupCache[cacheKey] = options;
      }),
      shareReplay(1)
    );

    return this.dynamicLookupRequestCache[cacheKey];
  }

  getSupportingDocumentStatuses(): Observable<SupportingDocumentGridStatusConfig> {
    if (this.documentStatusesCache) {
      return of(this.documentStatusesCache);
    }

    const storedStatuses = this.readStoredSupportingDocumentStatuses();
    if (storedStatuses) {
      this.documentStatusesCache = storedStatuses;
      return of(storedStatuses);
    }

    if (this.documentStatusesRequest$) {
      return this.documentStatusesRequest$;
    }

    this.documentStatusesRequest$ = this.httpService.get('/platform/master/lookup/category/DOCUMENT-STATUS').pipe(
      map((response: any) => this.buildSupportingDocumentStatuses(this.extractLookupRows(response))),
      tap((statuses: SupportingDocumentGridStatusConfig) => {
        this.documentStatusesCache = statuses;
        this.persistSupportingDocumentStatuses(statuses);
      }),
      shareReplay(1)
    );

    return this.documentStatusesRequest$;
  }

  clearSupportingDocumentLookupCache(): void {
    this.supportingDocumentCategoriesCache = null;
    this.supportingDocumentTypesCache = {};
    this.categoriesRequest$ = null;
    this.typeRequestCache = {};
    this.dynamicLookupRequestCache = {};
    this.dynamicLookupCache = {};
    this.documentStatusesCache = null;
    this.documentStatusesRequest$ = null;
    this.localService.removeItem(this.supportingDocumentCategoryCacheKey);
    this.localService.removeItem(this.supportingDocumentTypeCacheKey);
    this.localService.removeItem(this.supportingDocumentStatusCacheKey);
  }

  getPageMetadataForDocument(pageVersionId: string): Observable<{
    tabs: RuleBuilderTabOption[];
  }> {
    return this.getMetaDataOfWizardPage(pageVersionId).pipe(
      map((response: any) => this.extractTabDataFromWizard(response))
    );
  }

  getPageMetadataForTabPage(tabPageId: string): Observable<{
  sections: RuleBuilderPanelOption[];
  }> {
  return this.getMetaDataOfTabPage(tabPageId).pipe(
    map((response: any) => ({
      sections: this.extractTabPageData(response)
    }))
  );
}

  buildConditionJson(fieldKey: string, operator: string, rawValue: string): any {
    if (!fieldKey || !operator) {
      return null;
    }
    return {
      field: fieldKey,
      operator,
      value: this.normalizeRawValue(rawValue, operator)
    };
  }

  buildConditionGroup(
    conditions: Array<{ fieldKey: string; operator: string; fieldValue: any }>,
    logicalOperator: string
  ): RuleBuilderConditionGroup | null {
    const rules = (conditions || [])
      .map((condition: { fieldKey: string; operator: string; fieldValue: any }) =>
        this.buildConditionJson(condition.fieldKey, condition.operator, condition.fieldValue)
      )
      .filter((condition: RuleBuilderCondition | null): condition is RuleBuilderCondition => !!condition);

    if (!rules.length) {
      return null;
    }

    return {
      logicalOperator: String(logicalOperator || 'AND').trim().toUpperCase() === 'OR' ? 'OR' : 'AND',
      rules
    };
  }

  buildExpression(fieldKey: string, operator: string, rawValue: string): string {
    const trimmedValue = String(rawValue ?? '').trim();
    const serializedValue = this.serializeValue(trimmedValue);

    switch (operator) {
      case 'equals':
        return `data.${fieldKey} === ${serializedValue}`;
      case 'notEquals':
        return `data.${fieldKey} !== ${serializedValue}`;
      case 'contains':
        return `String(data.${fieldKey} || '').includes(${serializedValue})`;
      case 'isTrue':
        return `data.${fieldKey} === true`;
      case 'isFalse':
        return `data.${fieldKey} === false`;
      case 'isNotEmpty':
        return `data.${fieldKey} !== null && data.${fieldKey} !== undefined && String(data.${fieldKey}).trim() !== ''`;
      default:
        return '';
    }
  }

  private serializeValue(value: string): string {
    if (value === 'true' || value === 'false') {
      return value;
    }
    if (value !== '' && !Number.isNaN(Number(value))) {
      return value;
    }
    return JSON.stringify(value);
  }

  private normalizeRawValue(value: string, operator: string): any {
    const trimmed = String(value ?? '').trim();
    if (operator === 'isTrue') {
      return true;
    }
    if (operator === 'isFalse') {
      return false;
    }
    if (operator === 'isNotEmpty') {
      return '';
    }
    if (trimmed === 'true') {
      return true;
    }
    if (trimmed === 'false') {
      return false;
    }
    if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
      return Number(trimmed);
    }
    return trimmed;
  }

  private buildSupportingDocumentStatuses(rows: any[]): SupportingDocumentGridStatusConfig {
    const options = rows.map((item: any) => this.toSupportingDocumentOption(item));

    return {
      pendingReview: this.findLookupStatusValue(options, ['Pending Review']) || 'Pending Review',
      validated: this.findLookupStatusValue(options, ['Verified', 'Validated']) || 'Verified',
      deficient: this.findLookupStatusValue(options, ['Deficient']) || 'Deficient',
      archived: this.findLookupStatusValue(options, ['Archived']) || 'Archived',
      deleted: this.findLookupStatusValue(options, ['Deleted']) || 'Deleted',
      addressed: this.findLookupStatusValue(options, ['Addressed']) || 'Addressed',
      case: this.findLookupStatusValue(options, ['Case']) || 'Case'
    };
  }

  private findLookupStatusValue(options: SupportingDocumentGridOption[], candidates: string[]): string {
    const normalizedCandidates = candidates.map((item: string) => this.normalizeLookupText(item));
    const match = options.find((option: SupportingDocumentGridOption) => {
      const normalizedKey = this.normalizeLookupText(option?.key);
      const normalizedValue = this.normalizeLookupText(option?.value);
      return normalizedCandidates.includes(normalizedKey) || normalizedCandidates.includes(normalizedValue);
    });

    return String(match?.value || match?.key || '').trim();
  }

  private normalizeLookupText(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private toLookupOptions(rows: any[]): RuleBuilderOption[] {
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows.map((row: any) => ({
      label: row?.value || row?.label || row?.key || '',
      value: row?.key || row?.value || ''
    })).filter((row: RuleBuilderOption) => !!row.value);
  }

  private extractTabDataFromWizard(response: any): {
    tabs: RuleBuilderTabOption[];
  } {
    const rows = Array.isArray(response) ? response : response?.data?.tabconfig || [];
    const tabs: RuleBuilderTabOption[] = rows.map((item: any, index: number) => ({
      pageName: item?.name || `Page ${index + 1}`,
      pageId: String(item?.id),
    }));
    return { tabs };
  }

  private extractTabPageData(response: any): RuleBuilderPanelOption[] {
    const template = typeof response === 'string' ? JSON.parse(response) : response;
    const components = template?.data?.components || [];
    const sections: any[] = [];
    const getFields = (items: any[]): any[] => {
      let fields: any[] = [];
      items.forEach((comp: any) => {
        if (comp.type === 'columns' && comp.columns) {
          comp.columns.forEach((col: any) => {
            fields = fields.concat(getFields(col.components || []));
          });
          return;
        }
        if (comp.input && comp.type !== 'panel') {
          fields.push({
            fieldLabel: String(comp.label || comp.key || ''),
            fieldKey: String(comp.key || '')
          });
        }
        if (comp.components) {
          fields = fields.concat(getFields(comp.components));
        }
      });
      return fields;
    };
    const traverse = (items: any[]) => {
      items.forEach((item: any) => {
        if (item.type === 'panel') {
          sections.push({
            sectionNameLabel: item.title || item.label,
            sectionNameKey: item.key,
            sectionFields: getFields(item.components || [])
          });
        }
        if (item.components) {
          traverse(item.components);
        }
        if (item.columns) {
          item.columns.forEach((col: any) => {
            traverse(col.components || []);
          });
        }
      });
    };
    traverse(components);
    return sections;
  }

  private collectPanels(components: any[]): Array<{ key: string; label: string; components: any[] }> {
    const panels: Array<{ key: string; label: string; components: any[] }> = [];
    (components || []).forEach((component: any, index: number) => {
      if (!component) {
        return;
      }
      if (component.type === 'panel') {
        panels.push({
          key: String(component.key || component.label || `panel_${index + 1}`),
          label: String(component.label || component.title || component.key || `Panel ${index + 1}`),
          components: Array.isArray(component.components) ? component.components : []
        });
        return;
      }
      if (Array.isArray(component.components)) {
        panels.push(...this.collectPanels(component.components));
      }
      if (Array.isArray(component.columns)) {
        component.columns.forEach((column: any) => {
          if (Array.isArray(column?.components)) {
            panels.push(...this.collectPanels(column.components));
          }
        });
      }
    });
    return panels;
  }

  private collectFields(components: any[]): RuleBuilderFieldOption[] {
    const fields: RuleBuilderFieldOption[] = [];
    (components || []).forEach((component: any, index: number) => {
      if (!component) {
        return;
      }
      if (component.input === true && component.key) {
        fields.push({
          fieldLabel: String(component.label || component.key || `Field ${index + 1}`),
          fieldKey: String(component.key),
        });
      }
      if (Array.isArray(component.components)) {
        fields.push(...this.collectFields(component.components));
      }
      if (Array.isArray(component.columns)) {
        component.columns.forEach((column: any) => {
          if (Array.isArray(column?.components)) {
            fields.push(...this.collectFields(column.components));
          }
        });
      }
    });
    return fields;
  }

  private parseJson<T>(value: string, fallback: T): T {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }

  private extractLookupRows(response: any): any[] {
    if (Array.isArray(response?.data?.data)) {
      return response.data.data;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private toSupportingDocumentOption(item: any): SupportingDocumentGridOption {
    const key = String(item?.key ?? item?.value ?? item?.id ?? '').trim();
    const value = String(item?.value ?? item?.label ?? item?.name ?? item?.key ?? '').trim();
    const lookupId = String(item?.id ?? item?.lookupid ?? item?.lookup_id ?? key).trim();
    return {
      key,
      value,
      lookupId,
      parentId: String(item?.parentid ?? item?.parentId ?? item?.parent_id ?? '').trim(),
      optionaldata: this.parseLookupOptionalData(item?.optionaldata ?? item?.optional_data ?? item?.optionalData),
      lookup: []
    };
  }

  private parseLookupOptionalData(optionaldata: any): any {
    if (!optionaldata) {
      return null;
    }
    if (typeof optionaldata === 'object') {
      return optionaldata;
    }
    if (typeof optionaldata !== 'string') {
      return null;
    }
    try {
      return JSON.parse(optionaldata);
    } catch {
      return null;
    }
  }

  private persistSupportingDocumentCategories(categories: SupportingDocumentGridOption[]): void {
    this.localService.setItem(this.supportingDocumentCategoryCacheKey, JSON.stringify(categories));
  }

  private persistSupportingDocumentTypes(): void {
    this.localService.setItem(this.supportingDocumentTypeCacheKey, JSON.stringify(this.supportingDocumentTypesCache));
  }

  private persistSupportingDocumentStatuses(statuses: SupportingDocumentGridStatusConfig): void {
    this.localService.setItem(this.supportingDocumentStatusCacheKey, JSON.stringify(statuses));
  }

  private readStoredSupportingDocumentCategories(): SupportingDocumentGridOption[] {
    const raw = this.localService.getItem(this.supportingDocumentCategoryCacheKey);
    if (!raw) {
      return [];
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private readStoredSupportingDocumentTypes(): Record<string, SupportingDocumentGridOption[]> {
    if (Object.keys(this.supportingDocumentTypesCache).length) {
      return this.supportingDocumentTypesCache;
    }
    const raw = this.localService.getItem(this.supportingDocumentTypeCacheKey);
    if (!raw) {
      return {};
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      this.supportingDocumentTypesCache = parsed && typeof parsed === 'object' ? parsed : {};
      return this.supportingDocumentTypesCache;
    } catch {
      return {};
    }
  }

  private readStoredSupportingDocumentStatuses(): SupportingDocumentGridStatusConfig | null {
    const raw = this.localService.getItem(this.supportingDocumentStatusCacheKey);
    if (!raw) {
      return null;
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }
      return {
        pendingReview: String(parsed.pendingReview || '').trim(),
        validated: String(parsed.validated || '').trim(),
        deficient: String(parsed.deficient || '').trim(),
        archived: String(parsed.archived || '').trim(),
        deleted: String(parsed.deleted || '').trim(),
        addressed: String(parsed.addressed || '').trim(),
        case: String(parsed.case || '').trim()
      };
    } catch {
      return null;
    }
  }

  getMetaDataOfWizardPage(versionId: string) {
    return this.httpService.get(PageBuilderAddURL.EndPoints.page_config.pageVersion.replace('{id}', versionId));
  }

  getMetaDataOfTabPage(tabPageId: string) {
    return this.httpService.get(PageBuilderAddURL.EndPoints.supporting_document_rule.tabPageMetadata.replace('{id}', tabPageId));
  }


    listRules(
      query?: SupportingDocumentRuleApiQuery,
      endpointTemplate?: string
    ): Observable<SupportingDocumentRuleRecord[]> {
      const endpoint = query  && endpointTemplate
        ? this.buildListEndpoint(query, endpointTemplate)
        : this.resolveEndpointTemplate(endpointTemplate || '', query);

      return this.httpService.get(endpoint || PageBuilderAddURL.EndPoints.supporting_document_rule.getAllRulesForStepper).pipe(
        map((response: any) => {
          const rows = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response) ? response : [];
          return rows as SupportingDocumentRuleRecord[];
        })
      );
    }

    createRule(payload: SupportingDocumentRuleRecord): Observable<any> {
      return this.httpService.post(PageBuilderAddURL.EndPoints.supporting_document_rule.create, payload);
    }

    updateRule(ruleId: number | string, payload: SupportingDocumentRuleRecord): Observable<any> {
      return this.httpService.put(`${PageBuilderAddURL.EndPoints.supporting_document_rule.update.replace('{id}', String(ruleId))}`, payload);
    }

    deleteRule(ruleId: number | string): Observable<any> {
      return this.httpService.delete(`${PageBuilderAddURL.EndPoints.supporting_document_rule.delete.replace('{id}', String(ruleId))}`);
    }

    private buildListEndpoint(query?: SupportingDocumentRuleApiQuery, endpointTemplate?: string): string {
      const params = new URLSearchParams();
      if (query?.stepperId !== undefined && query?.stepperId !== null && String(query.stepperId).trim() !== '') {
        params.set('stepperId', String(query.stepperId));
      }
      if (query?.pageId !== undefined && query?.pageId !== null && String(query.pageId).trim() !== '') {
        params.set('pageId', String(query.pageId));
      }
      if (query?.panelKey !== undefined && query?.panelKey !== null && String(query.panelKey).trim() !== '') {
        params.set('panelKey', String(query.panelKey));
      }
      const queryString = params.toString();
      return queryString ? `${endpointTemplate}?${queryString}` : this.baseEndpoint;
    }

    private resolveEndpointTemplate(
      template: string,
      query?: SupportingDocumentRuleApiQuery
    ): string {
      return String(template || '')
        .replace('{stepperId}', encodeURIComponent(String(query?.stepperId ?? '')))
        .replace('{pageId}', encodeURIComponent(String(query?.pageId ?? '')))
        .replace('{panelKey}', encodeURIComponent(String(query?.panelKey ?? '')));
    }
}
